import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import { apiRequest } from "@/lib/api-client";
import type { components } from "@/types/api";
import type {
  Customer,
  CustomerCreateRequest,
  CustomerListItemWire,
  CustomerListQuery,
  CustomerPage,
  CustomerSummary,
  CustomerSummaryWire,
  CustomerTagCounts,
  CustomerTagCountsWire,
  CustomerUpdateRequest,
  CustomerWire,
} from "@/types/customer";
import type { Tag, TagWire } from "@/features/tag/types";

// Inlined (rather than imported from features/tag/api) to avoid a cycle:
// features/tag/api imports customerKeys from here.
function toTag(wire: TagWire): Tag {
  return {
    id: wire.id!,
    name: wire.name!,
    color: wire.color!,
    sortOrder: wire.sortOrder ?? 0,
  };
}

// ---------------------------------------------------------------------------
// Wire → view-model converters
//
// See the rationale block in `features/property/api.ts`: codegen emits
// optional scalars but the view-model promises `T | null` for ergonomic
// rendering. These mappers normalise `undefined → null` so the view-model
// contract holds at runtime.
// ---------------------------------------------------------------------------

export function toCustomer(wire: CustomerWire): Customer {
  return {
    id: wire.id!,
    fullName: wire.fullName!,
    phoneNumber: wire.phoneNumber!,
    email: wire.email ?? null,
    source: wire.source!,
    assignedBrokerId: wire.assignedBrokerId ?? null,
    notes: wire.notes ?? null,
    tags: (wire.tags ?? []).map(toTag),
    createdAt: wire.createdAt!,
    updatedAt: wire.updatedAt!,
  };
}

/**
 * The backend's CustomerListItemResponse is a subset of CustomerResponse
 * (no `notes`, `createdAt`); the view-model layer currently uses
 * `Customer` for both detail and list, with the missing fields defaulted
 * to null / the list's `updatedAt` so calls referencing them are safe.
 */
export function toCustomerListItem(wire: CustomerListItemWire): Customer {
  return {
    id: wire.id!,
    fullName: wire.fullName!,
    phoneNumber: wire.phoneNumber!,
    email: wire.email ?? null,
    source: wire.source!,
    assignedBrokerId: wire.assignedBrokerId ?? null,
    notes: null,
    tags: (wire.tags ?? []).map(toTag),
    createdAt: wire.updatedAt!,
    updatedAt: wire.updatedAt!,
  };
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const customerKeys = {
  all: ["customers"] as const,
  lists: () => [...customerKeys.all, "list"] as const,
  list: (query: CustomerListQuery) => [...customerKeys.lists(), query] as const,
  details: () => [...customerKeys.all, "detail"] as const,
  detail: (id: number) => [...customerKeys.details(), id] as const,
  summary: (id: number) => [...customerKeys.detail(id), "summary"] as const,
  tagCounts: (assignedBrokerId?: number) =>
    [...customerKeys.all, "tag-counts", assignedBrokerId ?? null] as const,
  // Prefix that matches every tag-counts query regardless of the
  // assignedBrokerId scope — used to invalidate them all at once.
  tagCountsAll: () => [...customerKeys.all, "tag-counts"] as const,
  searchByPhone: (phone: string) =>
    [...customerKeys.all, "search-by-phone", phone] as const,
};

// VN-mobile pattern (matches the rest of the codebase). Used to gate the
// phone-lookup query so we don't fire while the user is still typing.
const VN_PHONE_REGEX = /^(\+84|0)\d{9,10}$/;

// ---------------------------------------------------------------------------
// Network calls
// ---------------------------------------------------------------------------

function buildCustomerListPath(query: CustomerListQuery): string {
  const params = new URLSearchParams();
  if (query.page !== undefined) params.set("page", String(query.page));
  if (query.size !== undefined) params.set("size", String(query.size));
  if (query.search !== undefined && query.search !== "") params.set("search", query.search);
  if (query.tagId !== undefined) params.set("tagId", String(query.tagId));
  if (query.assignedBrokerId !== undefined) {
    params.set("assignedBrokerId", String(query.assignedBrokerId));
  }
  const qs = params.toString();
  return qs ? `/customers?${qs}` : "/customers";
}

async function fetchCustomerSummary(id: number): Promise<CustomerSummary> {
  const w = await apiRequest<CustomerSummaryWire>(`/customers/${id}/summary`);
  return {
    interestedProperties: w.interestedProperties ?? 0,
    leads: w.leads ?? 0,
    appointments: w.appointments ?? 0,
    activities: w.activities ?? 0,
  };
}

async function fetchCustomerTagCounts(
  assignedBrokerId?: number,
): Promise<CustomerTagCounts> {
  const path =
    assignedBrokerId !== undefined
      ? `/customers/tag-counts?assignedBrokerId=${assignedBrokerId}`
      : "/customers/tag-counts";
  const w = await apiRequest<CustomerTagCountsWire>(path);
  const byTagId: Record<number, number> = {};
  for (const [tagId, count] of Object.entries(w.byTagId ?? {})) {
    byTagId[Number(tagId)] = count;
  }
  return { total: w.total ?? 0, byTagId };
}

async function fetchCustomers(query: CustomerListQuery): Promise<CustomerPage> {
  const page = await apiRequest<
    components["schemas"]["PageResponseCustomerListItemResponse"]
  >(buildCustomerListPath(query));
  return {
    ...page,
    items: (page.items ?? []).map(toCustomerListItem),
  };
}

async function fetchCustomer(id: number): Promise<Customer> {
  return toCustomer(await apiRequest<CustomerWire>(`/customers/${id}`));
}

async function postCustomer(body: CustomerCreateRequest): Promise<Customer> {
  return toCustomer(
    await apiRequest<CustomerWire>("/customers", { method: "POST", body }),
  );
}

async function patchCustomer(
  id: number,
  body: CustomerUpdateRequest,
): Promise<Customer> {
  return toCustomer(
    await apiRequest<CustomerWire>(`/customers/${id}`, { method: "PATCH", body }),
  );
}

function deleteCustomerApi(id: number): Promise<void> {
  return apiRequest<void>(`/customers/${id}`, { method: "DELETE" });
}

// ---------------------------------------------------------------------------
// Cache invalidation helper. Used by every mutation to keep list queries
// consistent and warm the detail cache for the touched customer.
// ---------------------------------------------------------------------------

function syncCustomerIntoCache(queryClient: QueryClient, customer: Customer): void {
  queryClient.setQueryData(customerKeys.detail(customer.id), customer);
  void queryClient.invalidateQueries({ queryKey: customerKeys.lists() });
  // A create/update may change tag membership (e.g. tagging from the detail
  // panel), which shifts the per-segment counts behind the list's filter
  // chips. Refresh them so the chips update without a manual reload.
  void queryClient.invalidateQueries({ queryKey: customerKeys.tagCountsAll() });
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export function useCustomers(query: CustomerListQuery) {
  return useQuery({
    queryKey: customerKeys.list(query),
    queryFn: () => fetchCustomers(query),
    placeholderData: (prev) => prev,
  });
}

export function useCustomer(id: number | undefined) {
  return useQuery({
    queryKey: id !== undefined ? customerKeys.detail(id) : customerKeys.details(),
    queryFn: () => {
      if (id === undefined) throw new Error("useCustomer called without an id");
      return fetchCustomer(id);
    },
    enabled: id !== undefined,
  });
}

export function usePrefetchCustomer(): (id: number) => void {
  const queryClient = useQueryClient();
  return (id: number) => {
    queryClient.prefetchQuery({
      queryKey: customerKeys.detail(id),
      queryFn: () => fetchCustomer(id),
    });
  };
}

/** Aggregate counts for the detail panel's stat cards. */
export function useCustomerSummary(id: number | undefined) {
  return useQuery({
    queryKey: id !== undefined ? customerKeys.summary(id) : customerKeys.details(),
    queryFn: () => {
      if (id === undefined) throw new Error("useCustomerSummary called without an id");
      return fetchCustomerSummary(id);
    },
    enabled: id !== undefined,
  });
}

/**
 * Per-segment counts for the list's filter chips. Scoped server-side: an
 * agent sees only their own book; OWNER/MANAGER may pass an agent's id.
 */
export function useCustomerTagCounts(assignedBrokerId?: number) {
  return useQuery({
    queryKey: customerKeys.tagCounts(assignedBrokerId),
    queryFn: () => fetchCustomerTagCounts(assignedBrokerId),
    placeholderData: (prev) => prev,
  });
}

export function useCreateCustomer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CustomerCreateRequest) => postCustomer(body),
    onSuccess: (customer) => {
      syncCustomerIntoCache(queryClient, customer);
    },
  });
}

export function useUpdateCustomer(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CustomerUpdateRequest) => patchCustomer(id, body),
    onSuccess: (customer) => {
      syncCustomerIntoCache(queryClient, customer);
    },
  });
}

/**
 * Lookup a customer by exact phone-number match. The API's `search` param
 * is a substring filter across name + phone, so we fetch a small page and
 * filter to an exact phone equality on the client. Returns `null` when
 * there's no exact match (cleaner than an empty array — the hook is
 * "loaded with no result" when the query resolves to null).
 *
 * Only enabled when the phone matches the VN regex; this avoids a wasted
 * request on every keystroke. Callers should debounce the input.
 */
export function useSearchCustomerByPhone(phone: string | undefined) {
  const normalised = phone?.replace(/\s+/g, "") ?? "";
  const enabled = normalised.length > 0 && VN_PHONE_REGEX.test(normalised);
  return useQuery({
    queryKey: customerKeys.searchByPhone(normalised),
    enabled,
    queryFn: async (): Promise<Customer | null> => {
      const page = await fetchCustomers({ search: normalised, size: 10 });
      return (
        page.items.find((c) => c.phoneNumber === normalised) ?? null
      );
    },
    staleTime: 60_000,
  });
}

export function useDeleteCustomer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteCustomerApi(id),
    onSuccess: (_data, id) => {
      queryClient.removeQueries({ queryKey: customerKeys.detail(id) });
      void queryClient.invalidateQueries({ queryKey: customerKeys.lists() });
      // Deleting a customer drops them from the segment counts too.
      void queryClient.invalidateQueries({ queryKey: customerKeys.tagCountsAll() });
    },
  });
}
